import { useEffect } from "react";
import {
  keepPreviousData,
  useQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import {
  listEnrichedOrders,
  listOrderSummaries,
  getEnrichedOrderDetail,
  addItemToOrder,
  updateOrderItem,
  setOrderItemBonus,
  replaceOrderItem,
  getLegacyKgMigrationPreview,
  migrateLegacyKgItem,
  removeOrderItem,
  completeOrder,
  reopenOrder,
  updateOrder,
  deleteOrder,
  getChangeRequest,
  getChangeRequestItems,
  createChangeRequest,
  updateChangeRequest,
  deleteChangeRequest,
  approveChangeRequest,
  rejectChangeRequest,
} from "./api";
import type {
  AddItemToOrderPayload,
  UpdateOrderItemPayload,
  SetOrderItemBonusPayload,
  ReplaceOrderItemPayload,
  LegacyKgMigrationPayload,
  CreateChangeRequestPayload,
  RejectChangeRequestPayload,
  OrderSummaryFilters,
} from "./types";
import { isOperationalEnrichedOrderPage } from "./types";

export const ORDERS_KEY = ["orders"] as const;
export const ORDER_SUMMARIES_KEY = [...ORDERS_KEY, "summaries"] as const;
export const ORDER_DETAIL_KEY = [...ORDERS_KEY, "enriched-detail"] as const;
export const LEGACY_KG_MIGRATION_PREVIEW_KEY = [
  ...ORDERS_KEY,
  "legacy-kg-migration-preview",
] as const;
const ALERTS_KEY = ["alerts"];
const ALERTS_COUNT_KEY = ["alerts", "count"];

function normalizeOrderSummaryFilters(
  filters?: OrderSummaryFilters,
): OrderSummaryFilters | undefined {
  if (!filters) return undefined;

  const search = filters.search?.trim();
  const statuses = [...new Set(filters.statuses?.filter(Boolean) ?? [])].sort();
  const normalized = {
    ...(filters.client_id ? { client_id: filters.client_id } : {}),
    ...(filters.store_id ? { store_id: filters.store_id } : {}),
    ...(filters.status ? { status: filters.status } : {}),
    ...(statuses.length > 0 ? { statuses } : {}),
    ...(search ? { search } : {}),
    ...(filters.date_from ? { date_from: filters.date_from } : {}),
    ...(filters.date_to ? { date_to: filters.date_to } : {}),
  };

  return Object.keys(normalized).length > 0 ? normalized : undefined;
}

function isSummaryOrDetailKey(queryKey: readonly unknown[]) {
  return (
    queryKey[0] === ORDERS_KEY[0] &&
    (queryKey[1] === "summaries" || queryKey[1] === "enriched-detail")
  );
}

function invalidateOrderReadModels(
  qc: ReturnType<typeof useQueryClient>,
  orderId?: string,
) {
  qc.invalidateQueries({ queryKey: ORDERS_KEY });
  if (orderId) {
    qc.invalidateQueries({ queryKey: [...ORDER_DETAIL_KEY, orderId] });
  }
}

function invalidateDirectOrderMutationReadModels(
  qc: ReturnType<typeof useQueryClient>,
  orderId?: string,
) {
  invalidateOrderReadModels(qc, orderId);
  qc.invalidateQueries({ queryKey: ["checkins"] });
  qc.invalidateQueries({ queryKey: ALERTS_KEY });
  qc.invalidateQueries({ queryKey: ALERTS_COUNT_KEY });
}

export function getOptimisticBasePrice(quotationItem: any) {
  return quotationItem?.unit_type === "kg"
    ? (quotationItem?.price_per_kg ?? quotationItem?.price_per_unit ?? 0)
    : (quotationItem?.price_per_box ?? 0);
}

export function useOrders(params?: { status?: string; search?: string }) {
  return useQuery({
    queryKey: [...ORDERS_KEY, params],
    queryFn: () => listEnrichedOrders(100, 0, params),
  });
}

export function useOrderSummaries({
  limit,
  offset,
  filters,
  enabled = true,
}: {
  limit: number;
  offset: number;
  filters?: OrderSummaryFilters;
  enabled?: boolean;
}) {
  const appliedFilters = normalizeOrderSummaryFilters(filters);

  return useQuery({
    queryKey: [
      ...ORDER_SUMMARIES_KEY,
      { limit, offset, filters: appliedFilters },
    ],
    queryFn: ({ signal }) =>
      listOrderSummaries(limit, offset, appliedFilters, signal),
    enabled,
    placeholderData: keepPreviousData,
  });
}

export function orderDetailQueryOptions(orderId: string) {
  return {
    queryKey: [...ORDER_DETAIL_KEY, orderId],
    queryFn: () => getEnrichedOrderDetail(orderId),
  };
}

export function useEnrichedOrderDetail(orderId: string | null | undefined) {
  return useQuery({
    ...(orderId
      ? orderDetailQueryOptions(orderId)
      : {
          queryKey: [...ORDER_DETAIL_KEY, orderId],
          queryFn: () => getEnrichedOrderDetail(orderId!),
        }),
    enabled: !!orderId,
  });
}

export function legacyKgMigrationPreviewQueryOptions(
  orderId: string,
  itemId: string,
) {
  return {
    queryKey: [...LEGACY_KG_MIGRATION_PREVIEW_KEY, orderId, itemId],
    queryFn: () => getLegacyKgMigrationPreview(orderId, itemId),
  };
}

export function useLegacyKgMigrationPreview(
  orderId: string | null | undefined,
  itemId: string | null | undefined,
) {
  return useQuery({
    ...(orderId && itemId
      ? legacyKgMigrationPreviewQueryOptions(orderId, itemId)
      : {
          queryKey: [...LEGACY_KG_MIGRATION_PREVIEW_KEY, orderId, itemId],
          queryFn: () => getLegacyKgMigrationPreview(orderId!, itemId!),
        }),
    enabled: !!orderId && !!itemId,
  });
}

export function usePrefetchEnrichedOrderDetails(orderIds: string[]) {
  const qc = useQueryClient();
  const stableOrderIds = orderIds.join("|");

  useEffect(() => {
    if (!stableOrderIds) return;

    stableOrderIds.split("|").forEach((orderId) => {
      qc.prefetchQuery(orderDetailQueryOptions(orderId));
    });
  }, [qc, stableOrderIds]);
}

export function useAddItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: AddItemToOrderPayload) => addItemToOrder(payload),
    onMutate: async (payload) => {
      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await qc.cancelQueries({ queryKey: ORDERS_KEY });

      // Snapshot the previous value
      const previousOrders = qc.getQueriesData({ queryKey: ORDERS_KEY });

      // Try to find the item details in the quotations cache to make the optimistic update richer
      // We look for any query that starts with ["quotations"] and has an items array
      let qi: any = null;
      const quotationsData = qc.getQueriesData({ queryKey: ["quotations"] });
      for (const [, data] of quotationsData) {
        if (
          data &&
          typeof data === "object" &&
          "items" in data &&
          Array.isArray(data.items)
        ) {
          qi = data.items.find(
            (item: any) => item.id === payload.quotation_item_id,
          );
          if (qi) break;
        }
      }

      // Optimistically update to the new value
      previousOrders.forEach(([queryKey]) => {
        if (isSummaryOrDetailKey(queryKey)) return;

        qc.setQueryData(queryKey, (old: any) => {
          if (!old || !old.items) return old;
          if (isOperationalEnrichedOrderPage(old)) return old;
          const basePrice = getOptimisticBasePrice(qi);

          return {
            ...old,
            items: old.items.map((order: any) => {
              if (order.store_id === payload.store_id) {
                const newItem = {
                  id: `optimistic-${Date.now()}`,
                  order_id: order.id,
                  quotation_item_id: payload.quotation_item_id,
                  quantity: payload.quantity,
                  bonus_quantity: 0,
                  product_name: qi?.product_name || "Adicionando...",
                  supplier_name: qi?.supplier_name || "...",
                  price_per_box: qi?.price_per_box || 0,
                  price_per_kg: qi?.price_per_kg,
                  price_per_unit: qi?.price_per_unit || 0,
                  computed_price: basePrice * payload.quantity,
                  charged_price: basePrice * payload.quantity,
                  billable_quantity: payload.quantity,
                  bonus_value: 0,
                  unit_type: qi?.unit_type || "un",
                  is_available: true,
                  return_status: "NONE",
                  final_price: basePrice * payload.quantity,
                  real_price: basePrice * payload.quantity,
                  created_at: new Date().toISOString(),
                  created_by: "",
                  updated_at: new Date().toISOString(),
                  updated_by: "",
                };

                return {
                  ...order,
                  items: [...(order.items || []), newItem],
                };
              }
              return order;
            }),
          };
        });
      });

      return { previousOrders };
    },
    onError: (_err, _newTodo, context) => {
      if (context?.previousOrders) {
        context.previousOrders.forEach(([queryKey, data]: [any, any]) => {
          qc.setQueryData(queryKey, data);
        });
      }
    },
    onSettled: (order) => {
      invalidateDirectOrderMutationReadModels(qc, order?.id);
    },
  });
}

export function useUpdateOrderItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      orderId,
      itemId,
      payload,
    }: {
      orderId: string;
      itemId: string;
      payload: UpdateOrderItemPayload;
    }) => updateOrderItem(orderId, itemId, payload),
    onSettled: (_item, _error, { orderId }) => {
      invalidateDirectOrderMutationReadModels(qc, orderId);
    },
  });
}

export function useSetOrderItemBonus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      orderId,
      itemId,
      payload,
    }: {
      orderId: string;
      itemId: string;
      payload: SetOrderItemBonusPayload;
    }) => setOrderItemBonus(orderId, itemId, payload),
    onSettled: (_item, _error, { orderId }) => {
      invalidateDirectOrderMutationReadModels(qc, orderId);
    },
  });
}

export function useReplaceOrderItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      orderId,
      itemId,
      payload,
    }: {
      orderId: string;
      itemId: string;
      payload: ReplaceOrderItemPayload;
    }) => replaceOrderItem(orderId, itemId, payload),
    onSettled: (_item, _error, { orderId }) => {
      invalidateDirectOrderMutationReadModels(qc, orderId);
    },
  });
}

export function useMigrateLegacyKgItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      orderId,
      itemId,
      payload,
    }: {
      orderId: string;
      itemId: string;
      payload: LegacyKgMigrationPayload;
    }) => migrateLegacyKgItem(orderId, itemId, payload),
    onSuccess: (_item, { orderId, itemId }) => {
      invalidateDirectOrderMutationReadModels(qc, orderId);
      qc.invalidateQueries({
        queryKey: [...LEGACY_KG_MIGRATION_PREVIEW_KEY, orderId, itemId],
      });
    },
  });
}

export function useRemoveOrderItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ orderId, itemId }: { orderId: string; itemId: string }) =>
      removeOrderItem(orderId, itemId),
    onSettled: (_result, _error, { orderId }) => {
      invalidateDirectOrderMutationReadModels(qc, orderId);
    },
  });
}

export function useCompleteOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (orderId: string) => completeOrder(orderId),
    onSuccess: (_order, orderId) => {
      invalidateOrderReadModels(qc, orderId);
      qc.invalidateQueries({ queryKey: ["checkins"] });
    },
  });
}

export function useReopenOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (orderId: string) => reopenOrder(orderId),
    onSuccess: (_order, orderId) => {
      invalidateOrderReadModels(qc, orderId);
      qc.invalidateQueries({ queryKey: ["checkins"] });
    },
  });
}

export function useUpdateOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      orderId,
      payload,
    }: {
      orderId: string;
      payload: { status?: string; obs?: string };
    }) => updateOrder(orderId, payload),
    onSuccess: (_order, { orderId }) => {
      invalidateOrderReadModels(qc, orderId);
      qc.invalidateQueries({ queryKey: ["checkins"] });
    },
  });
}

export function useDeleteOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (orderId: string) => deleteOrder(orderId),
    onSuccess: (_result, orderId) => {
      invalidateOrderReadModels(qc, orderId);
      qc.invalidateQueries({ queryKey: ["checkins"] });
    },
  });
}

export function useChangeRequest(crId: string | null | undefined) {
  return useQuery({
    queryKey: ["change-request", crId],
    queryFn: () => getChangeRequest(crId!),
    enabled: !!crId,
  });
}

export function useChangeRequestItems(crId: string | null | undefined) {
  return useQuery({
    queryKey: ["change-request", crId, "items"],
    queryFn: () => getChangeRequestItems(crId!),
    enabled: !!crId,
  });
}

export function useCreateChangeRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      orderId,
      payload,
    }: {
      orderId: string;
      payload: CreateChangeRequestPayload;
    }) => createChangeRequest(orderId, payload),
    onSuccess: (changeRequest) =>
      invalidateOrderReadModels(qc, changeRequest.order_id),
  });
}

export function useUpdateChangeRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      crId,
      payload,
    }: {
      crId: string;
      payload: CreateChangeRequestPayload;
    }) => updateChangeRequest(crId, payload),
    onSuccess: (changeRequest) => {
      qc.setQueryData(["change-request", changeRequest.id], changeRequest);
      invalidateOrderReadModels(qc, changeRequest.order_id);
    },
  });
}

export function useDeleteChangeRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (crId: string) => deleteChangeRequest(crId),
    onSuccess: () => {
      invalidateOrderReadModels(qc);
      qc.invalidateQueries({ queryKey: ALERTS_KEY });
      qc.invalidateQueries({ queryKey: ALERTS_COUNT_KEY });
    },
  });
}

export function useApproveChangeRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (crId: string) => approveChangeRequest(crId),
    onSuccess: (changeRequest) => {
      invalidateOrderReadModels(qc, changeRequest.order_id);
      qc.invalidateQueries({ queryKey: ALERTS_KEY });
      qc.invalidateQueries({ queryKey: ALERTS_COUNT_KEY });
      qc.invalidateQueries({ queryKey: ["checkins"] });
    },
  });
}

export function useRejectChangeRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      crId,
      payload,
    }: {
      crId: string;
      payload: RejectChangeRequestPayload;
    }) => rejectChangeRequest(crId, payload),
    onSuccess: (changeRequest) => {
      invalidateOrderReadModels(qc, changeRequest.order_id);
      qc.invalidateQueries({ queryKey: ALERTS_KEY });
      qc.invalidateQueries({ queryKey: ALERTS_COUNT_KEY });
    },
  });
}
